import { NO_DEVICE_CONTROLS } from '~/util/captureControls';
/** The engine's working day starts at this hour (tools/engine_job.py DAY_START_HOUR). */
export const DAY_START_HOUR = 4;
/** Minutes the time zone is ahead of UTC at a given instant. */
function offsetMinutes(at, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(at);
    const n = (type) => Number(parts.find(p => p.type === type)?.value);
    const asUtc = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
    return Math.round((asUtc - at.getTime()) / 60000);
}
/** The instant (ISO, UTC) the period measured ends: 04:00 on the day after
 * `lastDay` (YYYY-MM-DD) in `timeZone`. Null for a date that isn't one. */
export function periodEnd(lastDay, timeZone) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(lastDay || '');
    if (!m)
        return null;
    const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1, DAY_START_HOUR, 0, 0);
    // The offset at that wall time; a second pass settles a daylight-saving change between guess and answer.
    let at = wall - offsetMinutes(new Date(wall), timeZone) * 60000;
    at = wall - offsetMinutes(new Date(at), timeZone) * 60000;
    return new Date(at).toISOString();
}
/** The devices whose end date isn't the period's end yet, each with the controls to save. */
export function endsToSet(devices, ends) {
    const target = new Date(ends).getTime();
    return devices
        .filter(d => d.status === 'active')
        .filter(d => {
        const own = d.capture_controls?.ends_at;
        return !own || new Date(own).getTime() !== target;
    })
        .map(d => ({
        id: d.id,
        controls: { ...(d.capture_controls ?? NO_DEVICE_CONTROLS), ends_at: ends },
    }));
}
/** Brings every active device of a client to the period's end. Returns how many were changed. */
export async function syncTrackingEnd(orgId, settings, listDevices, setControls) {
    if (!settings?.measure_to || !settings.time_zone)
        return 0;
    const ends = periodEnd(settings.measure_to, settings.time_zone);
    if (!ends)
        return 0;
    const changes = endsToSet(await listDevices(orgId), ends);
    for (const c of changes) {
        await setControls(orgId, c.id, c.controls);
    }
    return changes.length;
}
/** Every client the dashboard can open, each brought to its period's end, one after another. A client that
 * can't be read (no key here, no verified engine, a network error) is skipped and tried at the next load.
 * Returns how many computers changed. */
export async function syncAllTrackingEnds(clients, listDevices, setControls) {
    let changed = 0;
    for (const client of clients) {
        try {
            changed += await syncTrackingEnd(client.orgId, await client.settings(), listDevices, setControls);
        }
        catch (err) {
            console.warn(`[Zunox] couldn't set the tracking end date for ${client.orgId}:`, err);
        }
    }
    return changed;
}
